/**
 * GlobalTrialBanner — single PlanHub-style trial banner shown on every
 * authenticated page (except /login, /landing, /trial-expired and /billing).
 *
 * Replaces the per-page <TrialBanner> mounted on Procurement Scanner.
 * Lives in App.js inside <BrowserRouter> so useLocation is available.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Sparkles, X, ArrowRight, Crown } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

function MasterBanner({ onDismiss }) {
  return (
    <>
      <style>{`body { padding-top: 32px !important; }`}</style>
      <div
        data-testid="master-banner-global"
        className="bg-gradient-to-r from-[#0A1233] to-[#1B3A8A] text-white px-4 py-1.5 flex items-center gap-3 fixed top-0 left-0 right-0 z-[9999] border-b border-[#C9A24E]/50"
      >
        <Crown className="w-3.5 h-3.5 text-[#C9A24E] shrink-0" />
        <div className="flex-1 min-w-0 text-[12px] leading-tight truncate">
          <b className="text-[#C9A24E]">Acesso Master</b>
          <span className="text-white/85"> — todas as ferramentas liberadas · créditos ilimitados · Claire &amp; Auto Pilot sempre ativos</span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="master-banner-dismiss"
          title="Ocultar por hoje"
          className="shrink-0 p-1 rounded hover:bg-white/10 opacity-70 hover:opacity-100"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "alp.trial-global.dismissed";

// Routes that should NOT render the global banner (already provide context
// or would be redundant).
const SUPPRESSED_PREFIXES = [
  "/login", "/signup", "/landing", "/trial-expired", "/billing",
  "/master",  // master console has its own admin context — never on trial
];

// Canonical master/admin emails — these accounts are ALWAYS exempt from
// trial gating regardless of what role is currently stored in the JWT.
// Defensive: a downgraded role in production (Iter98 production drift) must
// never lock the platform owner out of his own app.
const HARDCODED_MASTERS = new Set([
  "master@agentelead.pro",
  "master-demo@agentelead.pro",
]);
const ADMIN_ROLES = new Set(["super_admin", "admin", "master", "master_demo"]);

const isAdminUser = (u) =>
  !!u && (ADMIN_ROLES.has(u.role) || HARDCODED_MASTERS.has((u.email || "").toLowerCase()));

export default function GlobalTrialBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [trial, setTrial] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = useAuth();
  const user = auth?.user || null;
  const userIsAdmin = isAdminUser(user);

  // Refetch trial status whenever the route changes — keeps the banner
  // accurate after a `/billing` upgrade returns the user to /dashboard.
  useEffect(() => {
    let cancelled = false;
    // Anonymous visitors have no trial — skip the call (avoids 401 noise
    // on every public pageview).
    let token = "";
    try { token = window.localStorage.getItem("alp.access_token") || ""; } catch (_e) { /* ignore */ }
    if (!token) { setTrial(null); return () => {}; }
    axios.get(`${API}/trial/status`)
      .then(({ data }) => { if (!cancelled) setTrial(data); })
      .catch(() => { if (!cancelled) setTrial(null); });
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Hard-redirect to /trial-expired when the trial is over (admins exempt).
  // Iter98 — Also exempt any user whose JWT role OR email matches the
  // canonical master set. This survives a production DB drift where the
  // master's role got downgraded to `user`.
  useEffect(() => {
    if (!trial) return;
    if (trial.is_admin || userIsAdmin) return;
    if (!trial.expired) return;
    const allow = ["/trial-expired", "/billing", "/login", "/landing", "/signup", "/auth/callback"];
    if (!allow.some((p) => location.pathname.startsWith(p))) {
      navigate("/trial-expired", { replace: true });
    }
  }, [trial, location.pathname, navigate, userIsAdmin]);

  // Per-day dismiss
  useEffect(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      setDismissed(window.localStorage.getItem(DISMISS_KEY) === today);
    } catch (_e) { /* ignore */ }
  }, []);

  const dismiss = () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      window.localStorage.setItem(DISMISS_KEY, today);
    } catch (_e) { /* ignore */ }
    setDismissed(true);
  };

  if (!trial || !trial.started || trial.expired) {
    if (userIsAdmin && !SUPPRESSED_PREFIXES.some((p) => location.pathname.startsWith(p)) && !dismissed) {
      return <MasterBanner onDismiss={dismiss} />;
    }
    return null;
  }
  if (trial.is_admin || userIsAdmin) {
    if (!SUPPRESSED_PREFIXES.some((p) => location.pathname.startsWith(p)) && !dismissed) {
      return <MasterBanner onDismiss={dismiss} />;
    }
    return null;
  }
  if (dismissed) return null;
  if (SUPPRESSED_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const days = trial.days_remaining;
  const urgent = days <= 2;

  return (
    <>
      {/* Inject body padding so the fixed banner doesn't overlap content. */}
      <style>{`body { padding-top: 38px !important; }`}</style>
      <div
        data-testid="trial-banner-global"
        className={`${urgent ? "from-rose-900 to-rose-700" : "from-[#0F172A] to-[#1E293B]"} bg-gradient-to-r text-white px-4 py-2 flex items-center gap-3 fixed top-0 left-0 right-0 z-[9999] border-b ${urgent ? "border-rose-400/40" : "border-amber-500/30"}`}
      >
        <Lock className="w-3.5 h-3.5 text-amber-400 shrink-0" />
        <div className="flex-1 min-w-0 text-[12px] leading-tight">
          You're on the <b className="text-amber-300">14-day free trial</b>
          {typeof days === "number" && (
            <> · <span className="text-amber-200" data-testid="trial-banner-global-days">
              {days === 0 ? "ends today" : `${days} day${days === 1 ? "" : "s"} left`}
            </span></>
          )}
          <span className="hidden md:inline">
            . Sensitive lead fields are <b>locked</b> and downloads are disabled.
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/billing")}
          data-testid="trial-banner-global-upgrade"
          className="shrink-0 inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 text-[11.5px] font-bold uppercase tracking-wide"
        >
          <Sparkles className="w-3 h-3" /> Upgrade <ArrowRight className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="trial-banner-global-dismiss"
          title="Hide for today"
          className="shrink-0 p-1 rounded hover:bg-white/10 opacity-70 hover:opacity-100"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </>
  );
}
